import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { applyConsent, ConsentChoices, OPEN_PREFERENCES_EVENT, readConsent, saveConsent } from '../../utils/consent';
type CategoryKey = keyof ConsentChoices;
const categories: Array<{
  key: CategoryKey;
  title: string;
  description: string;
}> = [{
  key: 'analytics',
  title: 'Analytics',
  description: 'Google Analytics and Microsoft Clarity. Measure visits and record anonymised sessions and heatmaps so we can see where the site is unclear. Text on the page is masked in recordings.'
}, {
  key: 'marketing',
  title: 'Marketing',
  description: 'LinkedIn Insight Tag. Measures the results of our LinkedIn advertising and allows us to show relevant adverts to professionals who have visited the site.'
}];
export default function CookieConsent() {
  const [isOpen, setIsOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [choices, setChoices] = useState<ConsentChoices>({
    analytics: false,
    marketing: false
  });
  const headingRef = useRef<HTMLHeadingElement>(null);
  const reduceMotion = useReducedMotion();

  // On first load: apply a stored choice, or ask.
  useEffect(() => {
    const stored = readConsent();
    if (stored) {
      setChoices(stored);
      applyConsent(stored);
    } else {
      setIsOpen(true);
    }
  }, []);

  // Footer "Cookie preferences" link reopens the panel with details expanded.
  useEffect(() => {
    const handleOpen = () => {
      const stored = readConsent();
      if (stored) setChoices(stored);
      setShowDetails(true);
      setIsOpen(true);
    };
    window.addEventListener(OPEN_PREFERENCES_EVENT, handleOpen);
    return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, handleOpen);
  }, []);
  useEffect(() => {
    if (isOpen) headingRef.current?.focus();
  }, [isOpen, showDetails]);
  const commit = (next: ConsentChoices) => {
    setChoices(next);
    setIsOpen(false);
    setShowDetails(false);
    saveConsent(next);
  };
  const buttonBase = 'min-h-[44px] px-5 py-2.5 rounded-md text-sm font-semibold transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2';
  const primaryButton = `${buttonBase} bg-primary text-white hover:bg-primary-light`;
  const secondaryButton = `${buttonBase} border-2 border-primary text-primary bg-white hover:bg-gray-50`;
  return <AnimatePresence data-id="element-318" data-mp="1iz9qoa38qt1t">
      {isOpen && <motion.section role="region" aria-labelledby="cookie-consent-heading" initial={reduceMotion ? {
      opacity: 0
    } : {
      opacity: 0,
      y: 16
    }} animate={reduceMotion ? {
      opacity: 1
    } : {
      opacity: 1,
      y: 0
    }} exit={reduceMotion ? {
      opacity: 0
    } : {
      opacity: 0,
      y: 16
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} className="fixed inset-x-0 bottom-0 z-50 p-3 md:p-5" data-id="element-319" data-mp="140xu03fvbkan">
          <div className="mx-auto max-w-4xl max-h-[85vh] overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-xl" data-id="element-320" data-mp="rqq00yfcc1cj">
            <div className="p-5 md:p-6" data-id="element-321" data-mp="11g9m0cycobbm">
              <h2 id="cookie-consent-heading" ref={headingRef} tabIndex={-1} className="text-lg font-serif font-bold text-primary focus:outline-none" data-id="element-322" data-mp="11hb616xcg85h">
                {showDetails ? 'Cookie preferences' : 'Your privacy'}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-dark" data-id="element-323" data-mp="16etbgnc5k58q">
                We use essential storage to run this site. With your
                permission, we would also like to use analytics and marketing
                cookies to understand how solicitors use the site and to measure
                our advertising. Nothing optional is loaded until you choose.
                Read our{' '}
                <Link to="/policies/privacy#cookies" className="font-semibold text-primary underline hover:text-secondary" data-id="element-324" data-mp="3h9ibjsg4bcpn">
                  Privacy and Cookie Policy
                </Link>
                .
              </p>

              {showDetails && <fieldset className="mt-5 divide-y divide-gray-200 border-y border-gray-200" data-id="element-325" data-mp="3skwhjqp98u4d">
                  <legend className="sr-only" data-id="element-326" data-mp="1e3gcmugouzu">Choose cookie categories</legend>
                  <div className="flex items-start justify-between gap-6 py-4" data-id="element-327" data-mp="2zzwkcer6lyy5">
                    <div data-id="element-328" data-mp="34xy3ezolxy9m">
                      <p className="text-sm font-semibold text-primary" data-id="element-329" data-mp="34v24b810x3jl">
                        Strictly necessary
                      </p>
                      <p className="mt-1 text-sm leading-relaxed text-slate-dark" data-id="element-330" data-mp="3enhmo6g8fu50">
                        Remembers your cookie choice. Always on, because the site
                        cannot respect your choice without it.
                      </p>
                    </div>
                    <span className="shrink-0 pt-0.5 text-sm font-semibold text-slate-dark" data-id="element-331" data-mp="13bk74i57ylja">
                      Always on
                    </span>
                  </div>
                  {categories.map(category => <div key={category.key} className="flex items-start justify-between gap-6 py-4" data-id="element-332" data-mp="ua2x9hieng31">
                      <div data-id="element-333" data-mp="kcof9dgu2j8m">
                        <label htmlFor={`consent-${category.key}`} className="text-sm font-semibold text-primary" data-id="element-334" data-mp="kcy7l7pcp2hh">
                          {category.title}
                        </label>
                        <p id={`consent-${category.key}-description`} className="mt-1 text-sm leading-relaxed text-slate-dark" data-id="element-335" data-mp="bi0eo0m7dpq5">
                          {category.description}
                        </p>
                      </div>
                      <button id={`consent-${category.key}`} type="button" role="switch" aria-checked={choices[category.key]} aria-describedby={`consent-${category.key}-description`} onClick={() => setChoices(prev => ({
                ...prev,
                [category.key]: !prev[category.key]
              }))} className={`relative mt-0.5 inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2 ${choices[category.key] ? 'bg-primary' : 'bg-gray-300'}`} data-id="element-336" data-mp="3j4vwmxly6zkx">
                        <span className="sr-only" data-id="element-337" data-mp="tftu5xmju2ol">
                          {choices[category.key] ? 'On' : 'Off'}
                        </span>
                        <span aria-hidden="true" className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform duration-150 ${choices[category.key] ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-338" data-mp="2e45xmm14z56r" />
                      </button>
                    </div>)}
                </fieldset>}

              {/* Accept and Reject carry equal weight, per ICO guidance. */}
              <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end" data-id="element-339" data-mp="256lypjqqxkzb">
                {showDetails ? <button type="button" className={secondaryButton} onClick={() => commit(choices)} data-id="element-340" data-mp="3gjy4w19zafon">
                    Save choices
                  </button> : <button type="button" className={`${buttonBase} text-primary underline hover:text-secondary sm:mr-auto sm:px-0`} onClick={() => setShowDetails(true)} data-id="element-341" data-mp="18wmokkgbgutr">
                    Manage preferences
                  </button>}
                <button type="button" className={primaryButton} onClick={() => commit({
              analytics: false,
              marketing: false
            })} data-id="element-342" data-mp="1smyniq78f8ko">
                  Reject all
                </button>
                <button type="button" className={primaryButton} onClick={() => commit({
              analytics: true,
              marketing: true
            })} data-id="element-343" data-mp="21o6baec4gr0i">
                  Accept all
                </button>
              </div>
            </div>
          </div>
        </motion.section>}
    </AnimatePresence>;
}